import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import BASE_URL from '../config/config'
import { Bar } from 'react-chartjs-2'
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

// Departamentos que NO queremos mostrar
const DEPTS_EXCLUIDOS = ['no_es_dpto_de_venta', 'fundador']

// Mapeo de departamentos a sus horas vendidas
const MAP_DEPARTAMENTOS_A_VENDIDAS = {
  seo: 'HorasSEO',
  creativo: 'HorasCreativos',
  rrss: 'HorasRRSS',
  desarrollo: 'HorasDesarrollo',
  sem: 'HorasSEM',
}

// Mapeo de nombres personalizados para cada departamento
const CUSTOM_DEPT_NAMES = {
  seo: 'DPTO SEO',
  creativo: 'DPTO CREATIVO',
  rrss: 'DPTO REDES SOCIALES',
  desarrollo: 'DPTO DESARROLLO',
  sem: 'DPTO SEM',
}

const Ventas = () => {
  const [empleadosData, setEmpleadosData] = useState(null)
  const [horasVendidas, setHorasVendidas] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empleadosRes, horasRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/ventas/dpto`),
          axios.get(`${BASE_URL}/api/ventas/horas-vendidas`),
        ])
        setEmpleadosData(empleadosRes.data)
        setHorasVendidas(horasRes.data)
      } catch (error) {
        console.error('Error al obtener datos:', error)
        setError('Hubo un error al cargar los datos.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const obtenerDepartamentosValidos = useMemo(() => {
    if (!empleadosData) return []
    return Object.keys(empleadosData.horasPorDepartamento).filter(
      (dept) => !DEPTS_EXCLUIDOS.includes(dept)
    )
  }, [empleadosData])

  const totalHorasDptos = useMemo(() => {
    if (!empleadosData) return 0
    return obtenerDepartamentosValidos.reduce(
      (acc, dept) => acc + (empleadosData.horasPorDepartamento[dept] || 0),
      0
    )
  }, [empleadosData, obtenerDepartamentosValidos])

  const totalHorasVendidas = useMemo(() => {
    if (!horasVendidas) return 0
    return Object.values(horasVendidas).reduce((acc, horas) => acc + (horas || 0), 0)
  }, [horasVendidas])

  const diferenciaTotal = useMemo(() => {
    return totalHorasDptos - totalHorasVendidas
  }, [totalHorasDptos, totalHorasVendidas])

  const diferenciaPorDepartamento = (dept) => {
    if (!empleadosData || !horasVendidas) return { resta: 0, vendida: 0 }
  
    const horasDepto = empleadosData.horasPorDepartamento[dept] || 0;
    const claveVendida = MAP_DEPARTAMENTOS_A_VENDIDAS[dept];
    const vendida = claveVendida ? (horasVendidas[claveVendida] || 0) : 0;
  
    return { resta: horasDepto - vendida, vendida }
  };

  const chartData = useMemo(() => {
    if (!empleadosData || !horasVendidas) return null
    return {
      labels: obtenerDepartamentosValidos.map(dept => CUSTOM_DEPT_NAMES[dept] || dept),
      datasets: [
        {
          label: 'Horas Dpto',
          data: obtenerDepartamentosValidos.map(dept => empleadosData.horasPorDepartamento[dept] || 0),
          backgroundColor: '#419efb',
        },
        {
          label: 'Horas Vendidas',
          data: obtenerDepartamentosValidos.map(dept => {
            const claveVendida = MAP_DEPARTAMENTOS_A_VENDIDAS[dept]
            return claveVendida ? (horasVendidas[claveVendida] || 0) : 0
          }),
          backgroundColor: '#00b969',
        }
      ]
    }
  }, [empleadosData, horasVendidas, obtenerDepartamentosValidos])

  if (loading) return <>
    <main className='bg-gris'>
      <div className="grid-ventas site-width">
        <h2>Ventas</h2>
        <p>Cálculo de capacidad de ventas por departamento.</p>
        <div className="summary-cards">
        {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} width={'100%'} height={80} />
            ))}
        </div>
        <div className="department-cards">
        {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} width={'100%'} height={120} />
            ))}
        </div>
        <div className="chart-container">
        {Array.from({ length: 1 }).map((_, i) => (
                <Skeleton key={i} width={'100%'} height={320} />
            ))}
        </div>
        </div>
        </main>
  </>
  if (error) return <p className="error">{error}</p>

  return (
    <main className='bg-gris'>
      <div className="grid-ventas site-width">
        <h2>Ventas</h2>
        <p>Cálculo de capacidad de ventas mensual por departamento.</p>
        <div className="summary-cards">
          <div className="card-summary">
            <h3>Total Horas Departamentos</h3>
            <p>{totalHorasDptos.toFixed(1)}h</p>
          </div>
          <div className="card-summary">
            <h3>Total Horas Vendidas</h3>
            <p>{totalHorasVendidas.toFixed(1)}h</p>
          </div>
          <div className="card-summary">
            <h3>Diferencia</h3>
            <p>{diferenciaTotal.toFixed(1)}h</p>
          </div>
        </div>

        {/* Tarjetas por departamento */}
        <div className="department-cards">
        {obtenerDepartamentosValidos.map((dept) => {
            const { resta, vendida } = diferenciaPorDepartamento(dept);
            const horasDepto = empleadosData.horasPorDepartamento[dept] || 0;
            
            return (
            <div className="card-dept" key={dept}>
                <h4>{CUSTOM_DEPT_NAMES[dept] || dept}</h4>
                <p className='hora-limite'>Horas límite: {horasDepto}h<br/><br/><span>(Suma de horas por contrato de personas que contribuyen en el dpto al mes)</span></p>
                <p className='horas-vendidas'>Horas vendidas: {vendida}h</p>
                <p className={resta >= 0 ? 'diferencia-positiva' : 'diferencia-negativa'}>
                  Puedes vender: {resta >= 0 ? `+${resta.toFixed(1)}` : resta.toFixed(1)}h
                </p>
            </div>
            );
        })}
        </div>

        {/* Gráfico de barras */}
        <div className="chart-container">
          <h3>Gráfica comparativa de ventas</h3>
          {chartData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  title: { display: false },
                },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          ) : (
            <>
            <h2>Ventas</h2>
            <p>Cálculo de capacidad de ventas por departamento.</p>
            </>
            
          )}
        </div>
      </div>
    </main>
  )
}

export default Ventas
